import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCircle } from '@fortawesome/pro-light-svg-icons';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { useProgramsAndReports } from 'shared/hooks/useProgramsAndReports';
import AddProgramsModal from './AddProgramsModal';
import AddReportModal from './AddReportModal';
import {
  dismissReportTask,
  isReportTaskDismissed,
} from './dismissedReportTasks';
import { hasActionableThirdPartyTasks } from './ThirdPartyTodoTasksUtils';
import { UserProgram } from '../ProgramsAndReports/types';

// When true, hides task rows that are either completed or disabled (not yet actionable).
const HIDE_INACTIVE_TASKS = true;

// When false, completed programs are hidden from the "Profile status" list.
const SHOW_COMPLETED_PROGRAM_TASKS = false;

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;

  .task-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${SPACERS.medium};
    padding: ${SPACERS.medium} 0;
    border-bottom: 1px solid ${COLORS.greyLight};
  }
  .task-row:last-child,
  .task-row.last {
    border-bottom: none;
    padding-bottom: 0;
  }
  .task-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: ${SPACERS.tiny};
  }
  .task-title {
    display: flex;
    align-items: center;
    gap: ${SPACERS.tiny};
  }
  .done-icon {
    font-size: 16px;
  }
  .task-action {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: ${SPACERS.tiny};
  }
  .active-message-list {
    margin: 0;
    padding-left: ${SPACERS.medium};
    list-style: disc;
  }
  .active-message-list li {
    margin: 0;
  }
  .profile-status-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: ${SPACERS.extraTiny};
  }
  .profile-status-list li {
    display: flex;
    align-items: center;
    gap: ${SPACERS.tiny};
  }
  .profile-status-list .status-icon {
    font-size: 14px;
  }
`;

interface ThirdPartyTodoData {
  hasAnyProgram: boolean;
  programsWithoutReports: UserProgram[];
  programsWithoutCurrentYearReport: UserProgram[];
}

const ProgramProfileList: React.FC<{
  programs: UserProgram[];
}> = ({ programs }) => {
  if (programs.length === 0) return null;
  const allCompleted = programs.every(p => p.program.isCompleted);
  const visiblePrograms = SHOW_COMPLETED_PROGRAM_TASKS
    ? programs
    : programs.filter(p => !p.program.isCompleted);
  if (visiblePrograms.length === 0) return null;
  const heading = allCompleted
    ? 'All program profiles are complete:'
    : 'Profile status:';
  return (
    <>
      <TrueImpactText variant="body-small" color="greyDark">
        {heading}
      </TrueImpactText>
      <ul className="profile-status-list">
        {visiblePrograms.map(p => (
          <li key={p.program.dbId}>
            <FontAwesomeIcon
              icon={p.program.isCompleted ? faCheckCircle : faCircle}
              color={p.program.isCompleted ? COLORS.success : COLORS.greyBase}
              className="status-icon"
            />
            <TrueImpactText variant="body-small" color="greyDark">
              {p.program.name}
            </TrueImpactText>
          </li>
        ))}
      </ul>
    </>
  );
};

interface ThirdPartyTodoTasksProps {
  className?: string;
  programs: UserProgram[];
  reloadPrograms: () => void;
}

const ThirdPartyTodoTasks: React.FC<ThirdPartyTodoTasksProps> = ({
  className,
  programs,
  reloadPrograms,
}) => {
  const { push } = useHistory();
  const { path: reportsPath } = useProgramsAndReports();

  const [showAddPrograms, setShowAddPrograms] = useState(false);
  const [showAddReport, setShowAddReport] = useState(false);
  const [addReportProgram, setAddReportProgram] = useState<
    UserProgram | undefined
  >(undefined);
  const [dismissedVersion, setDismissedVersion] = useState(0);

  const { organizationName, isBBUser } = useUserActions();
  const partnerName = isBBUser ? 'Blackbaud' : 'Charity Navigator';

  const data = useMemo<ThirdPartyTodoData>(() => {
    const currentYear = new Date().getFullYear();
    const programsWithoutReports = programs.filter(
      p => !p.reports || p.reports.length === 0,
    );
    const programsWithoutCurrentYearReport = programs.filter(
      p =>
        !p.reports?.some(r => {
          if (r.year == null) return false;
          const y =
            typeof r.year === 'number' ? r.year : parseInt(String(r.year), 10);
          return y === currentYear;
        }),
    );
    return {
      hasAnyProgram: programs.length > 0,
      programsWithoutReports,
      programsWithoutCurrentYearReport,
    };
  }, [programs]);

  const currentYear = new Date().getFullYear();
  const incompletePrograms = programs.filter(p => !p.program.isCompleted);
  const allProfilesComplete =
    data.hasAnyProgram && incompletePrograms.length === 0;

  const addProgramsDone = data.hasAnyProgram;
  const completeProfilesActive =
    data.hasAnyProgram && incompletePrograms.length > 0;
  const completeProfilesDone = allProfilesComplete;

  // dismissedVersion in dep just to signal re-computation; read from LS each render.
  void dismissedVersion;
  const hasCurrentYearReport = (p: UserProgram) =>
    !!p.reports?.some(r => {
      if (r.year == null) return false;
      const y =
        typeof r.year === 'number' ? r.year : parseInt(String(r.year), 10);
      return y === currentYear;
    });
  const reportRowPrograms = programs.filter(
    p =>
      hasCurrentYearReport(p) ||
      !isReportTaskDismissed(p.program.dbId, currentYear),
  );

  if (!hasActionableThirdPartyTasks(programs, currentYear)) return null;

  const handleOpenAddReport = (p: UserProgram) => {
    setAddReportProgram(p);
    setShowAddReport(true);
  };
  const handleDismissReport = (p: UserProgram) => {
    dismissReportTask(p.program.dbId, currentYear);
    setDismissedVersion(v => v + 1);
  };

  return (
    <StyledDiv className={className}>
      <TaskRow
        title={`Add ${organizationName || 'your organization'}'s programs`}
        description={'List each core program or initiative your organization runs. Once added, you\'ll create profiles for each before ' +
          'reporting on that program.'}
        active={!addProgramsDone}
        done={addProgramsDone}
        doneMessage={`Adding programs makes them searchable on ${partnerName}`}
        button={
          <TrueImpactButton
            variant="primary-large"
            disabled={addProgramsDone}
            onClick={() => setShowAddPrograms(true)}
          >
            Add programs
          </TrueImpactButton>
        }
      />
      <AddProgramsModal
        open={showAddPrograms}
        onClose={() => setShowAddPrograms(false)}
        onCreated={reloadPrograms}
      />
      <TaskRow
        title="Complete program profiles"
        description="A profile task will be created for each program you add."
        active={completeProfilesActive}
        done={completeProfilesDone}
        disabledMessage="Available after programs are added"
        activeMessage={<ProgramProfileList programs={programs} />}
        doneMessage={<ProgramProfileList programs={programs} />}
        button={
          <TrueImpactButton
            variant="primary-large"
            disabled={!completeProfilesActive}
            onClick={() => push(reportsPath)}
          >
            Start
          </TrueImpactButton>
        }
      />
      {reportRowPrograms.length === 0 ? (
        <TaskRow
          title="Add an impact report (optional)"
          description="Once a profile is complete, you can add an Impact Snapshot or full report to show donors your outcomes."
          active={false}
          done={
            data.hasAnyProgram &&
            data.programsWithoutCurrentYearReport.length === 0
          }
          disabledMessage={
            !data.hasAnyProgram
              ? 'Available after at least one profile is complete'
              : `You've either added a ${currentYear} report for every program or dismissed the remaining reminders.`
          }
          doneMessage={`You've added a ${currentYear} report for every program`}
          button={
            <TrueImpactButton variant="primary-large" disabled>
              Add report
            </TrueImpactButton>
          }
          isLast
        />
      ) : (
        reportRowPrograms.map((p, idx) => {
          const isCompleted = p.program.isCompleted;
          const reportDone = hasCurrentYearReport(p);
          const isLast = idx === reportRowPrograms.length - 1;
          return (
            <TaskRow
              key={p.program.dbId}
              title={`Add a ${currentYear} impact report — ${p.program.name} (optional)`}
              description={
                reportDone
                  ? `${p.program.name} has a ${currentYear} report.`
                  : isCompleted
                  ? `${p.program.name} has a complete profile but no ${currentYear} report yet.`
                  : `Complete ${p.program.name}'s profile to add a ${currentYear} report.`
              }
              active={!reportDone && isCompleted}
              done={reportDone}
              disabledMessage="Available after this program's profile is complete"
              activeMessage={`Add a ${currentYear} report for ${p.program.name}.`}
              doneMessage={`You've added a ${currentYear} report for ${p.program.name}`}
              button={
                reportDone ? (
                  <TrueImpactButton variant="primary-large" disabled>
                    Add report
                  </TrueImpactButton>
                ) : (
                  <>
                    {isCompleted && (
                      <TrueImpactButton
                        variant="secondary-large"
                        onClick={() => handleDismissReport(p)}
                      >
                        Dismiss
                      </TrueImpactButton>
                    )}
                    <TrueImpactButton
                      disabled={!isCompleted}
                      variant="primary-large"
                      onClick={() => handleOpenAddReport(p)}
                    >
                      Add report
                    </TrueImpactButton>
                  </>
                )
              }
              isLast={isLast}
            />
          );
        })
      )}
      <AddReportModal
        open={showAddReport}
        onClose={() => setShowAddReport(false)}
        program={addReportProgram}
        year={currentYear}
        onCreated={reloadPrograms}
      />
      <ManageProgramsAndReportsLink />
    </StyledDiv>
  );
};

interface TaskRowProps {
  title: string;
  description: string;
  active: boolean;
  done: boolean;
  doneMessage?: React.ReactNode;
  disabledMessage?: string;
  activeMessage?: React.ReactNode;
  button: React.ReactNode;
  isLast?: boolean;
}

const TaskRow: React.FC<TaskRowProps> = ({
  title,
  description,
  active,
  done,
  doneMessage,
  disabledMessage,
  activeMessage,
  button,
  isLast,
}) => {
  const disabled = !active && !done;
  if (HIDE_INACTIVE_TASKS && !active) return null;
  const textColor = disabled ? 'greyBase' : 'blackBase';
  const descColor = disabled ? 'greyBase' : 'greyDark';
  return (
    <div className={`task-row${isLast ? ' last' : ''}`}>
      <div className="task-body">
        <div className="task-title">
          {done && (
            <FontAwesomeIcon
              icon={faCheckCircle}
              color={COLORS.success}
              className="done-icon"
            />
          )}
          <TrueImpactText variant="body-bold" color={textColor}>
            {title}
          </TrueImpactText>
        </div>
        <TrueImpactText variant="body-small" color={descColor}>
          {description}
        </TrueImpactText>
        {done &&
          doneMessage &&
          (typeof doneMessage === 'string' ? (
            <TrueImpactText variant="body-small-bold" color="success">
              {doneMessage}
            </TrueImpactText>
          ) : (
            <>{doneMessage}</>
          ))}
        {active &&
          activeMessage &&
          (typeof activeMessage === 'string' ? (
            <TrueImpactText variant="body-small" color="greyDark">
              {activeMessage}
            </TrueImpactText>
          ) : (
            <>{activeMessage}</>
          ))}
        {disabled && disabledMessage && (
          <TrueImpactText variant="body-small" color="greyBase">
            {disabledMessage}
          </TrueImpactText>
        )}
      </div>
      <div className="task-action">{button}</div>
    </div>
  );
};

const ManageLinkWrapper = styled.div`
  .manage-link-row {
    display: flex;
    justify-content: center;
    padding-top: ${SPACERS.medium};
    margin-top: ${SPACERS.small};
  }
  .manage-link {
    display: inline-flex;
    align-items: center;
    gap: ${SPACERS.extraTiny};
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: ${COLORS.ctaPrimary};
    font-weight: 600;
    font-size: 14px;
    transition: color 0.15s ease, transform 0.15s ease;
  }
  .manage-link:hover {
    text-decoration: underline;
  }
  .manage-link:hover .arrow {
    transform: translateX(3px);
  }
  .manage-link .arrow {
    transition: transform 0.15s ease;
    display: inline-block;
  }
`;

export const ManageProgramsAndReportsLink: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { push } = useHistory();
  const { path: reportsPath } = useProgramsAndReports();
  return (
    <ManageLinkWrapper className={className}>
      <div className="manage-link-row">
        <button
          type="button"
          className="manage-link"
          onClick={() => push(reportsPath)}
        >
          Manage all programs and reports
          <span className="arrow" aria-hidden>
            →
          </span>
        </button>
      </div>
    </ManageLinkWrapper>
  );
};

export default ThirdPartyTodoTasks;
